import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.withCredentials = true;

const UserContext = createContext();

export function UserProvider(props) {
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  //Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      try {
        const res = await axios.get("/user/me");
        if (res.data) {
          setUser({
            ...res.data,
            isAuthenticated: true,
          });
          const settingsRes = await axios.get("/user/get-settings");
          setSettings(settingsRes.data);
        } else {
          setUser({ isAuthenticated: false });
          setSettings({});
        }
      } catch (error) {
        console.error("Error checking auth status:", error);
        // ensure user is reset then perform logout side effects
        setUser({ isAuthenticated: false });
        setSettings({});
        try {
          await logout();
        } catch (e) {
          // ignore logout errors here
        }
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const res = await axios.post("/user/login", { email, password });
      const userData = res.data;

      if (userData) {
        //update user state
        setUser({ ...userData.user, isAuthenticated: true });
        // Use settings from login response to avoid race condition
        if (userData.settings) {
          setSettings(userData.settings);
        }
        setLoading(false);
        return true;
      }
      throw new Error("Login failed: No token received");
    } catch (error) {
      console.error("Login failed:", error);
      setUser({ isAuthenticated: false });
      setSettings({});
      setLoading(false);
      throw error.response?.data?.message || "Login failed";
    }
  }, []);

  const logout = useCallback(async () => {
    await axios.post("/user/logout");
    setUser({ isAuthenticated: false });
    setSettings({});
    setLoading(false);
    //Make sure to put the redirect to the component that calls this function
    // history.push("/login");
  }, []);

  const updateSettings = useCallback(async () => {
    try {
      const settingsRes = await axios.get("/user/get-settings");
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Failed to update settings:", error);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      loading,
      settings,
      updateSettings,
    }),
    [user, login, logout, settings, updateSettings]
  );

  return (
    <UserContext.Provider value={value}>{props.children}</UserContext.Provider>
  );
}

export const useUserContext = () => useContext(UserContext);
